import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  List,
  message,
  Modal,
  Row,
  Skeleton,
  Space,
  Statistic,
  Tag,
  Typography,
} from "antd";
import {
  ArrowLeftOutlined,
  BankOutlined,
  CheckCircleOutlined,
  CreditCardOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "contexts/AuthContext";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import paths from "routes/paths";
import { fetchWorkspace } from "services/workspaceService";
import olreadiaService from "services/olreadiaService";
import paymentMethodsService from "services/paymentMethodsService";
import useAppConfig from "hooks/access-control/useAppConfig";
import {
  organizationName,
  readable,
} from "./workspaceUtils";
import { OLIVEEB_COLORS, workspaceButtonStyles, workspaceStyles } from "./colors";

const { Text } = Typography;

const STRIPE_JS_URL = "https://js.stripe.com/v3/";
let stripeJsPromise;

const loadStripeJs = () => {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Stripe can only be loaded in the browser."));
  }
  if (window.Stripe) return Promise.resolve(window.Stripe);
  if (stripeJsPromise) return stripeJsPromise;

  stripeJsPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${STRIPE_JS_URL}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Stripe));
      existing.addEventListener("error", () => reject(new Error("Stripe.js failed to load.")));
      return;
    }

    const script = document.createElement("script");
    script.src = STRIPE_JS_URL;
    script.async = true;
    script.onload = () => resolve(window.Stripe);
    script.onerror = () => reject(new Error("Stripe.js failed to load."));
    document.body.appendChild(script);
  });

  return stripeJsPromise;
};

const stripeCardOptions = {
  hidePostalCode: false,
  style: {
    base: {
      color: "#0F172A",
      fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: "16px",
      fontSmoothing: "antialiased",
      "::placeholder": {
        color: "#94A3B8",
      },
    },
    invalid: {
      color: "#D9291C",
      iconColor: "#D9291C",
    },
  },
};

const sectionTitle = (title) => (
  <Text strong style={{ color: OLIVEEB_COLORS.oliveebGreen }}>
    {title}
  </Text>
);

const paymentMethodId = (method) =>
  method?.id || method?.stripePaymentMethodId || method?.paymentMethodId || "";

const maskedMethod = (method) => {
  const brand = method?.merchant || method?.brand || method?.cardBrand || "Card";
  const last4 = method?.last4 || method?.cardLast4 || method?.accountLast4;
  return last4 ? `${brand} •••• ${last4}` : brand;
};

const expiry = (method) => {
  const month = method?.expiryMonth || method?.expMonth;
  const year = method?.expiryYear || method?.expYear;
  if (!month || !year) return "-";
  return `${String(month).padStart(2, "0")}/${year}`;
};

const isDefaultOutgoing = (method) =>
  Boolean(method?.defaultOutgoingPaymentMethod || method?.isDefaultForCurrency);

const isPersonalAccount = (account) =>
  String(account?.accountType || "").toUpperCase() === "PERSONAL";

const activeAccountUser = (account, fallbackUser) => {
  const users = account?.users || [];
  const active = users.find((member) => {
    const status = String(member?.membershipStatus || "").toUpperCase();
    return status === "ACTIVE" || status === "ACCEPTED" || status === "";
  });
  return active?.user || fallbackUser || {};
};

const userName = (user, fallback = "User") => {
  const name = [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return name || user?.name || user?.username || user?.email || user?.id || fallback;
};

const paymentContext = (account, fallbackUser, fallbackUserId, organization) => {
  if (!account) {
    return {
      type: "workspace",
      label: "Workspace",
      detail: "-",
      userId: fallbackUserId,
      organizationId: "",
      canManageMethods: false,
    };
  }

  if (isPersonalAccount(account)) {
    const ownerUser = activeAccountUser(account, fallbackUser);
    const ownerUserId = ownerUser?.id || fallbackUserId;
    return {
      type: "user",
      label: "Personal user",
      detail: userName(ownerUser, ownerUserId || "User"),
      userId: ownerUserId,
      organizationId: "",
      canManageMethods: Boolean(ownerUserId),
    };
  }

  return {
    type: "business",
    label: "Organization",
    detail: account.organizationId
      ? organizationName(organization, account.organizationId)
      : "Not linked",
    userId: "",
    organizationId: account.organizationId || "",
    canManageMethods: Boolean(account.organizationId),
  };
};

const WorkspacePaymentMethodsPage = () => {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { appConfig } = useAppConfig();
  const actorId = user?.id || user?.userId || "";
  const [account, setAccount] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [outgoingMethods, setOutgoingMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [defaultingId, setDefaultingId] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [addingMethod, setAddingMethod] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  const [stripeError, setStripeError] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const stripeRef = useRef(null);
  const stripeCardRef = useRef(null);
  const cardMountRef = useRef(null);
  const stripePublishableKey =
    appConfig?.stripePublishableKey || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";

  const loadData = useCallback(async () => {
    if (!workspaceId || !actorId) return;

    try {
      setLoading(true);
      const nextAccount = await fetchWorkspace(workspaceId, actorId);
      setAccount(nextAccount);

      let nextOrganization = null;
      if (!isPersonalAccount(nextAccount) && nextAccount.organizationId) {
        try {
          nextOrganization = await olreadiaService.organizations.get(
            nextAccount.organizationId,
          );
        } catch (error) {
          nextOrganization = null;
        }
      }
      setOrganization(nextOrganization);

      if (isPersonalAccount(nextAccount)) {
        const ownerUser = activeAccountUser(nextAccount, user);
        const outgoing = await paymentMethodsService.outgoing.list({
          actorId,
          userId: ownerUser?.id || actorId,
          workspaceId,
          page: 0,
          size: 50,
        });
        setOutgoingMethods(outgoing);
        return;
      }

      if (!nextAccount.organizationId) {
        setOutgoingMethods([]);
        return;
      }

      const outgoing = await paymentMethodsService.outgoing.list({
        actorId,
        organizationId: nextAccount.organizationId,
        workspaceId,
        page: 0,
        size: 50,
      });
      setOutgoingMethods(outgoing);
    } catch (error) {
      message.error(error?.message || "Failed to load payment methods.");
    } finally {
      setLoading(false);
    }
  }, [actorId, workspaceId, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!addOpen) return undefined;

    let active = true;
    setStripeReady(false);
    setStripeError("");
    setCardComplete(false);

    if (!stripePublishableKey) {
      setStripeError("Stripe publishable key is not configured.");
      return undefined;
    }

    loadStripeJs()
      .then((Stripe) => {
        if (!active) return;
        const stripe = Stripe(stripePublishableKey);
        const elements = stripe.elements();
        const card = elements.create("card", stripeCardOptions);
        stripeRef.current = stripe;
        stripeCardRef.current = card;

        card.on("change", (event) => {
          setCardComplete(Boolean(event.complete));
          setStripeError(event.error?.message || "");
        });

        if (cardMountRef.current) {
          card.mount(cardMountRef.current);
          setStripeReady(true);
        }
      })
      .catch((error) => {
        if (active) setStripeError(error?.message || "Stripe.js failed to load.");
      });

    return () => {
      active = false;
      if (stripeCardRef.current) {
        stripeCardRef.current.destroy();
        stripeCardRef.current = null;
      }
      stripeRef.current = null;
    };
  }, [addOpen, stripePublishableKey]);

  const defaultOutgoing = useMemo(
    () => outgoingMethods.find((method) => isDefaultOutgoing(method)) || null,
    [outgoingMethods],
  );

  const setDefaultOutgoing = async (method) => {
    const id = paymentMethodId(method);
    if (!id) {
      message.error("Payment method id is missing.");
      return;
    }

    try {
      setDefaultingId(id);
      const context = paymentContext(account, user, actorId);
      await paymentMethodsService.outgoing.setDefault({
        actorId,
        methodId: id,
        userId: context.userId,
        organizationId: context.organizationId,
        workspaceId,
      });
      message.success("Default payment method updated.");
      await loadData();
    } catch (error) {
      message.error(error?.message || "Failed to update default payment method.");
    } finally {
      setDefaultingId("");
    }
  };

  const closeAddPaymentMethod = () => {
    if (addingMethod) return;
    setAddOpen(false);
    setCardComplete(false);
    setStripeError("");
  };

  const addPaymentMethod = async () => {
    if (!stripeRef.current || !stripeCardRef.current) {
      message.error("Stripe card entry is not ready yet.");
      return;
    }

    if (!cardComplete) {
      message.error("Enter the full card details first.");
      return;
    }

    try {
      setAddingMethod(true);
      const billingName = userName(user, "");
      const { paymentMethod, error } = await stripeRef.current.createPaymentMethod({
        type: "card",
        card: stripeCardRef.current,
        billing_details: {
          name: billingName || undefined,
          email: user?.email || undefined,
        },
      });

      if (error || !paymentMethod?.id) {
        setStripeError(error?.message || "Stripe could not create a payment method.");
        message.error(error?.message || "Failed to create payment method.");
        return;
      }

      const context = paymentContext(account, user, actorId);
      await paymentMethodsService.outgoing.attach({
        actorId,
        userId: context.userId,
        organizationId: context.organizationId,
        workspaceId,
        paymentMethodId: paymentMethod.id,
      });
      message.success("Payment method added.");
      setAddOpen(false);
      setCardComplete(false);
      setStripeError("");
      await loadData();
    } catch (error) {
      message.error(error?.message || "Failed to add payment method.");
    } finally {
      setAddingMethod(false);
    }
  };

  const context = paymentContext(account, user, actorId, organization);

  return (
    <div className="project-page">
      <div className="d-flex justify-content-between align-items-center mb-3 mt-3 gap-3 flex-wrap">
        <WidgetSectionTitle
          icon="wallet"
          title="Workspace Payment Methods"
          subtitle="Outgoing payment methods for the selected workspace context"
          className="flex-1"
          titleStyle={{ color: OLIVEEB_COLORS.oliveebDarkGreen }}
          subtitleStyle={{ color: OLIVEEB_COLORS.oliveebGreen }}
        />
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(paths.workspaceDetails(workspaceId))}>
            Back
          </Button>
          <Button
            icon={<ReloadOutlined />}
            style={workspaceButtonStyles.refresh}
            onClick={loadData}
          >
            Refresh
          </Button>
        </Space>
      </div>

      {loading && !account ? (
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      ) : !account ? (
        <Card>
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Workspace not found" />
        </Card>
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          {!context.canManageMethods ? (
            <Alert
              type="warning"
              showIcon
              message={
                !isPersonalAccount(account) && !account.organizationId ? (
                  <Text style={{ color: OLIVEEB_COLORS.oliveebBlue, fontWeight: 800 }}>
                    This workspace is not linked to an organization.
                  </Text>
                ) : (
                  "Payment methods cannot be managed for this workspace yet."
                )
              }
              description={
                isPersonalAccount(account) ? (
                  "A workspace user could not be resolved for this personal workspace."
                ) : (
                  <Space direction="vertical" size={8} align="start">
                    <Text style={{ color: OLIVEEB_COLORS.oliveebBlue }}>
                      Payment methods can be added after an organization is linked to this workspace.
                    </Text>
                    <Button
                      size="small"
                      type="primary"
                      icon={<LinkOutlined />}
                      onClick={() => navigate(paths.workspaceOrganization(workspaceId))}
                      style={{
                        background: OLIVEEB_COLORS.oliveebBlue,
                        borderColor: OLIVEEB_COLORS.oliveebBlue,
                        color: OLIVEEB_COLORS.white,
                        fontWeight: 800,
                      }}
                    >
                      Link organization
                    </Button>
                  </Space>
                )
              }
              style={{ textAlign: "left" }}
            />
          ) : null}

          <Card style={workspaceStyles.softCard}>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <Statistic
                  title={context.label}
                  value={context.detail || "Not linked"}
                  prefix={<BankOutlined />}
                />
              </Col>
              <Col xs={24} md={8}>
                <Statistic
                  title="Outgoing Methods"
                  value={outgoingMethods.length}
                  prefix={<CreditCardOutlined />}
                />
              </Col>
            </Row>
          </Card>

          <Card
            title={sectionTitle("Outgoing Payment Methods")}
            extra={
              <Space wrap>
                {defaultOutgoing ? (
                  <Tag color="green">{maskedMethod(defaultOutgoing)} default</Tag>
                ) : (
                  <Tag color="gold">No default</Tag>
                )}
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={!context.canManageMethods}
                  style={{
                    borderColor: OLIVEEB_COLORS.oliveebBlue,
                    background: `linear-gradient(135deg, ${OLIVEEB_COLORS.oliveebBlue}, ${OLIVEEB_COLORS.oliveebGreen})`,
                    boxShadow: "0 10px 22px rgba(0, 136, 204, 0.18)",
                  }}
                  onClick={() => setAddOpen(true)}
                >
                  Add Payment Method
                </Button>
              </Space>
            }
            style={{ borderRadius: 8 }}
          >
            {outgoingMethods.length ? (
              <List
                dataSource={outgoingMethods}
                renderItem={(method) => {
                  const id = paymentMethodId(method);
                  const isDefault = isDefaultOutgoing(method);

                  return (
                    <List.Item>
                      <Card
                        size="small"
                        style={{
                          width: "100%",
                          borderRadius: 8,
                          borderColor: isDefault
                            ? OLIVEEB_COLORS.oliveebGreen
                            : OLIVEEB_COLORS.border,
                          background: isDefault ? OLIVEEB_COLORS.greenSoft2 : OLIVEEB_COLORS.card,
                        }}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <Text strong style={{ display: "block", wordBreak: "break-word" }}>
                              {maskedMethod(method)}
                            </Text>
                            <Space wrap size={[4, 4]} style={{ marginTop: 6 }}>
                              <Tag color="blue">{readable(method?.status, "Status")}</Tag>
                              <Tag color="cyan">Exp {expiry(method)}</Tag>
                              {isDefault ? <Tag color="green">Default outgoing</Tag> : null}
                            </Space>
                          </div>
                          <Button
                            type={isDefault ? "default" : "primary"}
                            icon={isDefault ? <CheckCircleOutlined /> : <CreditCardOutlined />}
                            disabled={isDefault}
                            loading={defaultingId === id}
                            onClick={() => setDefaultOutgoing(method)}
                          >
                            {isDefault ? "Default" : "Set default"}
                          </Button>
                        </div>
                      </Card>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={`No outgoing payment methods found for this ${context.type === "business" ? "organization" : "user"}`}
              />
            )}
          </Card>

          <Modal
            title="Add Outgoing Payment Method"
            open={addOpen}
            onCancel={closeAddPaymentMethod}
            okText="Add Payment Method"
            okButtonProps={{
              loading: addingMethod,
              disabled: !stripeReady || !cardComplete || Boolean(stripeError),
              style: {
                borderColor: OLIVEEB_COLORS.oliveebGreen,
                background: OLIVEEB_COLORS.oliveebGreen,
              },
            }}
            onOk={addPaymentMethod}
          >
            <Alert
              type={stripePublishableKey ? "info" : "warning"}
              showIcon
              message={`Add a card payment method to this ${context.type === "business" ? "organization" : "user"}.`}
              description={
                stripePublishableKey
                  ? "Enter card details securely through Stripe. Olreadia only saves the Stripe payment method reference."
                  : "Stripe publishable key is not configured for this environment."
              }
              style={{ marginBottom: 16 }}
            />

            <Text strong style={{ color: OLIVEEB_COLORS.oliveebDarkBlue }}>
              Card Details
            </Text>
            <div
              style={{
                marginTop: 8,
                padding: "15px 16px",
                border: `1px solid ${stripeError ? OLIVEEB_COLORS.oliveebRed : OLIVEEB_COLORS.oliveebGreen}`,
                borderRadius: 16,
                background: OLIVEEB_COLORS.card,
                boxShadow: "0 16px 34px rgba(15, 23, 42, 0.12)",
              }}
            >
              <div ref={cardMountRef} />
              {!stripeReady && stripePublishableKey ? (
                <Text type="secondary">Loading secure card field...</Text>
              ) : null}
            </div>
            {stripeError ? (
              <Text type="danger" style={{ display: "block", marginTop: 8 }}>
                {stripeError}
              </Text>
            ) : (
              <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
                Use the same secure Stripe card flow as Olreadia.
              </Text>
            )}
          </Modal>

        </Space>
      )}
    </div>
  );
};

export default WorkspacePaymentMethodsPage;
